/**
 * Die beiden ruhigen Ränder des geöffneten Termins (13.10.2026, aus
 * EditTerminDrawer gelöst): oben „wann" mit Bezügen und Verschieben/Kopieren,
 * unten Löschen und „weiterer Termin".
 *
 * Das Datum ist kein Eingabefeld mehr. Im Planer nimmt „Verschieben" den Termin
 * auf, und man tippt den Zieltag im Kalender an. Ohne Kalender (Termin aus dem
 * Arbeitsplan eines Projekts) klappt es ein Datumsfeld auf.
 */
import { useEffect, useState } from "react";
import { useDeleteTermin } from "../../hooks/usePlanung";
import type { Termin } from "../../lib/types";
import { TerminBezuege, Zeitspanne } from "./TerminTeile";
import { btnDanger, btnGhost, hinweisZeile } from "./planer-styles";
import { grossFor, h1 } from "./planer-utils";
import "../../styles/termin.css";

export function TerminKopfMeta({ t, datum, onDatum, von, bis, besichtigung, arbeitsplanTag, onAufnehmen, onVon, onBis, eigeneZeit, onKunde }: {
  t: Termin;
  datum: string;
  onDatum: (d: string) => void;
  von: string;
  bis: string;
  besichtigung: boolean;
  /** Name des Arbeitstags, wenn der Termin aus dem Arbeitsplan geöffnet ist. */
  arbeitsplanTag: string;
  onAufnehmen?: (modus: "verschieben" | "kopieren") => void;
  /** Von–Bis zum Einstellen — erst sichtbar, wenn man die Zeit oben antippt (14.10.2026). */
  onVon: (v: string) => void;
  onBis: (v: string) => void;
  /** Wie viele Eingeteilte eine eigene Zeit behalten. */
  eigeneZeit: number;
  onKunde?: (kundeId: string) => void;
}) {
  const [datumOffen, setDatumOffen] = useState(false);
  const [zeitOffen, setZeitOffen] = useState(false);
  /* Die Zeit kommt aus dem Formularstand, damit sie beim Ändern mitläuft. */
  return (
    <div className="pl-kopf-meta tm-meta">
      <div className="tm-wann">
        {Number.isNaN(new Date(`${datum}T12:00:00`).getTime()) ? "" : new Date(`${datum}T12:00:00`).toLocaleDateString("de-DE", { weekday: "short", day: "numeric", month: "short" })}
        {" · "}
        <button type="button" className={"tm-zeit-knopf" + (zeitOffen ? " is-offen" : "")} aria-expanded={zeitOffen} onClick={() => setZeitOffen((v) => !v)} title="Zeit ändern">
          {von}–{bis}
          <svg viewBox="0 0 16 16" width="13" height="13" aria-hidden="true"><path d="M11 2.5l2.5 2.5L6 12.5H3.5V10z" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinejoin="round" /></svg>
        </button>
        <span className="tm-wann-dauer"> · {h1(grossFor(datum, von, bis))} h</span>
      </div>
      {zeitOffen && (
        <div className="tm-zeit">
          <Zeitspanne von={von} bis={bis} onVon={onVon} onBis={onBis} />
          {eigeneZeit > 0 && (
            <div style={{ ...hinweisZeile, marginTop: 8, marginBottom: 0 }}>
              {eigeneZeit === 1 ? "Eine Person behält" : `${eigeneZeit} Personen behalten`} eine eigene Zeit — die ändert sich nur in ihrer Zeile.
            </div>
          )}
        </div>
      )}
      {besichtigung && <div className="tm-meta-zeile">Besichtigung — kein Projekt, Termin am Kunden</div>}
      {arbeitsplanTag && <div className="tm-meta-zeile">{arbeitsplanTag} im Arbeitsplan</div>}
      <TerminBezuege t={t} oben ohneOrt onKunde={onKunde} />
      <div className="tm-taten">
        <button
          type="button"
          className={"tm-tat" + (datumOffen ? " is-aktiv" : "")}
          onClick={() => (onAufnehmen ? onAufnehmen("verschieben") : setDatumOffen((v) => !v))}
          title={onAufnehmen ? "Termin schließen und im Kalender einen anderen Tag antippen" : "Termin auf einen anderen Tag legen"}
        >⤳ Verschieben</button>
        {onAufnehmen && (
          <button type="button" className="tm-tat" onClick={() => onAufnehmen("kopieren")} title="Termin schließen und im Kalender den Tag der Kopie antippen">
            ⧉ Kopieren
          </button>
        )}
      </div>
      {datumOffen && (
        <div className="tm-datum">
          <input
            type="date"
            className="tm-feld"
            value={datum}
            onChange={(e) => onDatum(e.target.value || datum)}
            aria-label="Neues Datum"
          />
          {datum !== (t.start_at || "").slice(0, 10) && (
            <div style={hinweisZeile}>
              Wird beim Speichern verschoben — die Einsätze der Personen wandern mit.
            </div>
          )}
        </div>
      )}
    </div>
  );
}

export function TerminFuss({ t, onGeloescht, onWeiterer }: {
  t: Termin;
  onGeloescht: () => void;
  /** Nur im Planer: zurück in den Tag, um dort einen weiteren Termin anzulegen. */
  onWeiterer?: () => void;
}) {
  const del = useDeleteTermin();
  const [confirm, setConfirm] = useState(false);
  return (
    <div className="tm-fuss">
      {!confirm ? <button onClick={() => setConfirm(true)} className="tm-loeschen">Termin löschen</button> :
        <span style={{ display: "flex", gap: 8, alignItems: "center", flexWrap: "wrap" }}>
          {/* Wer eingeteilt ist, bekommt beim Löschen eine Absage in die
              Team-App. Das gehört vor den Klick, nicht danach. */}
          <span style={{ fontSize: 13 }}>
            {(() => {
              const n = (t.slots || []).filter((s) => s.person_typ !== "inhaber").length;
              return n > 0
                ? `Sicher? ${n} ${n === 1 ? "Helfer wird" : "Helfer werden"} benachrichtigt.`
                : "Sicher?";
            })()}
          </span>
          <button onClick={() => del.mutate(t.id, { onSuccess: onGeloescht })} style={btnDanger}>{del.isPending ? "…" : "Ja, löschen"}</button>
          <button onClick={() => setConfirm(false)} style={btnGhost}>Nein</button>
        </span>}
      {onWeiterer && <button onClick={onWeiterer} className="tm-weiterer">+ Weiterer Termin an diesem Tag</button>}
    </div>
  );
}

type Ziel = "zurueck" | "schliessen";

/**
 * Wege aus dem Termin (14.10.2026, Benjamin: „Wenn ich eine Eingabe abbreche, muss
 * ich wirklich nur ein Level tiefer landen"). Zurück, Hintergrund und Esc führen
 * eine Ebene zurück (Tag bzw. Arbeitsplan), nur × schließt ganz. Gespeichert wird
 * automatisch; vor dem Verlassen wird ausstehendes noch gesichert. Klappt das
 * nicht, bleibt der Termin offen und die Leiste unten sagt warum.
 */
export function useTerminWege(sichern: () => Promise<boolean>, wege: Record<Ziel, () => void>, gesperrt: boolean) {
  const weg = (ziel: Ziel) => { void sichern().then((ok) => { if (ok) wege[ziel](); }); };
  useEffect(() => {
    const taste = (e: KeyboardEvent) => {
      if (e.key !== "Escape" || gesperrt) return;
      e.preventDefault();
      weg("zurueck");
    };
    window.addEventListener("keydown", taste);
    return () => window.removeEventListener("keydown", taste);
  });
  return { weg };
}
